import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import RouteTitle from '../components/RouteTitle';
import PaddedBox from '../components/PaddedBox';
import ChapterModule from '../components/ChapterModule';
import Material from '../components/Material';
import { useParams } from 'react-router-dom';
import { getFirestore, doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { auth } from '../firebaseConfig'; // Import auth from firebaseConfig
import usePageTitle from '../hooks/usePageTitle';

const Chapter = () => {
    const { id } = useParams();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [lessons, setLessons] = useState([]);
    const [completedLessons, setCompletedLessons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTab, setSelectedTab] = useState(0);
    const [articles, setArticles] = useState([]);
    const [hasMaterials, setHasMaterials] = useState(false);
    const [isPremium, setIsPremium] = useState(false);

    usePageTitle(title ? `${title} - LearnCMD` : 'LearnCMD');

    useEffect(() => {
        const fetchData = async () => {
            const db = getFirestore();
            const docRef = doc(db, 'modules', id);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const data = docSnap.data();
                setTitle(data.title);
                setDescription(data.description);

                const lessonIds = JSON.parse(data.lesson);
                const lessonPromises = lessonIds.map(async (lessonId) => {
                    const lessonRef = doc(db, 'lessons', lessonId);
                    const lessonSnap = await getDoc(lessonRef);
                    return lessonSnap.exists() ? { id: lessonId, ...lessonSnap.data() } : null;
                });

                const lessonsData = await Promise.all(lessonPromises);
                setLessons(lessonsData.filter(lesson => lesson !== null));

                if (data.materials) {
                    const materialIds = JSON.parse(data.materials);
                    const articlesCollection = collection(db, 'materials');
                    const q = query(articlesCollection, where('__name__', 'in', materialIds));
                    const articlesSnapshot = await getDocs(q);
                    let articlesList = articlesSnapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }));

                    articlesList = articlesList.sort((a, b) => materialIds.indexOf(a.id) - materialIds.indexOf(b.id));

                    setArticles(articlesList);
                    setHasMaterials(true);
                }
            } else {
                console.log('No such document!');
            }

            const user = auth.currentUser;
            if (user) {
                const userDocRef = doc(db, 'users', user.uid);
                const userDocSnap = await getDoc(userDocRef);

                if (userDocSnap.exists()) {
                    const userData = userDocSnap.data();
                    const completedLessonIds = JSON.parse(userData.lessons);
                    setCompletedLessons(completedLessonIds);
                    setIsPremium(userData.premium || false);
                }
            }

            setLoading(false);
        };

        fetchData();
    }, [id]);

    if (loading) {
        return <Box sx={{ paddingLeft: 10, paddingRight: 10, paddingTop: 3 }}>Loading...</Box>;
    }

    const completedCount = lessons.filter(lesson => completedLessons.includes(lesson.id)).length;
    const allCompleted = completedCount === lessons.length;

    let foundIncomplete = false;

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
    };

    return (
        <PaddedBox showBanner={!isPremium}>
            <RouteTitle title={title} returnable={true} />
            <Box sx={{ maxWidth: 600 }}>
                {description}
            </Box>
            <Box sx={{ height: 30 }} />
            <Box />
            <span style={{ color: '#333333' }}>Completed:</span> 
            <span style={{ color: allCompleted ? '#00739D' : '#A2423D' }}>
                {completedCount}/{lessons.length}
            </span>
            <Box />
            <Box sx={{ height: 30 }} />
            {hasMaterials && (
                <>           
                    <Tabs 
                        value={selectedTab} 
                        onChange={handleTabChange} 
                        sx={{ maxWidth: 600 }} 
                        TabIndicatorProps={{ style: { display: 'none' } }} // Disable animation
                    >
                        <Tab 
                            label="Lessons" 
                            disableRipple
                            sx={{ 
                                backgroundColor: selectedTab === 0 ? '#CCE0E0' : 'white', 
                                color: selectedTab === 0 ? '#006666 !important' : '#555555', 
                                borderRadius: 2, 
                                marginRight: 1,
                                fontFamily: 'Roboto',
                                textTransform: 'none',
                                fontWeight: 'bold',
                                width: 140
                            }} 
                        />
                        <Tab 
                            label="Extras" 
                            disableRipple
                            sx={{ 
                                backgroundColor: selectedTab === 1 ? '#CCE0E0' : 'white', 
                                color: selectedTab === 1 ? '#006666 !important' : '#555555', 
                                borderRadius: 2, 
                                marginRight: 1,
                                fontFamily: 'Roboto',
                                textTransform: 'none',
                                fontWeight: 'bold',
                                width: 140
                            }} 
                        />
                    </Tabs>
                    <Box sx={{ height: 30 }} />
                </>
            )}
            <Box sx={{ backgroundColor: '#EDEDED', borderRadius: 2, padding: 3 }}>
                {selectedTab === 0 ? (
                    lessons.sort((a, b) => a.number - b.number)
                        .map((lesson, index) => {
                            const isLastLesson = index === lessons.length - 1;
                            const isCompleted = completedLessons.includes(lesson.id);

                            if (!isCompleted) {
                                foundIncomplete = true;
                            }

                            // Get the next lesson if it exists
                            const nextLesson = index < lessons.length - 1 ? lessons[index + 1] : null;
                            // Check if the next lesson is completed
                            const nextLessonCompleted = nextLesson ? completedLessons.includes(nextLesson.id) : false;
                            
                            // We're already sorting lessons by number in the parent map function
                            // Use full line only if current and next lessons are completed
                            const line = isLastLesson ? "none" : (isCompleted && nextLessonCompleted ? "full" : "gray");

                            return (
                                <ChapterModule
                                    key={index}
                                    number={index + 1}
                                    title={lesson.name}
                                    content={lesson.description}
                                    route={`/module/${id}/lesson/${lesson.id}`}
                                    line={line}
                                    completed={isCompleted}
                                    disabled={lesson.premium && !isPremium}
                                />
                            );
                        })
                ) : (
                    articles.map((article, i) => (
                        <Material 
                            key={i} 
                            title={article.title} 
                            content={article.description} 
                            link={`/module/${id}/article/${article.id}`} 
                            disabled={article.premium && !isPremium}
                        />
                    ))
                )}
            </Box>
        </PaddedBox>
    );
};

export default Chapter;